import { useMemo } from 'react';
import dayjs from 'dayjs';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { TouchpointResponseAnalytics } from '@/lib/api/types';
import { fmtInt, type ThroughputPhase } from '@/features/insights/engine';
import { PHASE_COLORS } from '@/features/insights/charts/chartTokens';
import { useScrollRamp } from '@/features/insights/motion/useScrollRamp';

/** Un punto de la línea: el inicio del cubo (hora o día) y sus interactions. */
interface Punto {
  ms: number;
  count: number;
}

const MAX_PUNTOS = 120;

/**
 * Los cubos que manda el servidor, con los huecos rellenos a cero entre el
 * primero y el último para que la línea tenga la forma del tiempo y no la de
 * los datos. Si hay demasiados (semanas por hora) se dejan tal cual.
 */
function puntosDe(data: TouchpointResponseAnalytics): Punto[] {
  const paso = data.bucket === 'hour' ? 3_600_000 : 86_400_000;
  const crudos = data.timeSeries
    .map((b) => ({ ms: new Date(data.bucket === 'hour' ? b.bucket : `${b.bucket}T00:00:00`).getTime(), count: b.count }))
    .filter((b) => Number.isFinite(b.ms))
    .sort((a, b) => a.ms - b.ms);
  if (crudos.length === 0) return [];
  const primero = crudos[0]!.ms;
  const ultimo = crudos[crudos.length - 1]!.ms;
  const n = Math.floor((ultimo - primero) / paso) + 1;
  if (n > MAX_PUNTOS) return crudos;
  const porMs = new Map(crudos.map((b) => [b.ms, b.count]));
  return Array.from({ length: n }, (_, i) => ({ ms: primero + i * paso, count: porMs.get(primero + i * paso) ?? 0 }));
}

/**
 * «Interactions over time» de la estación, en el panel oscuro del informe:
 * una barra por hora (o por día) coloreada por fase —arranque · pico · cola—,
 * el pico como línea de techo y los marcadores debajo. Las barras suben con
 * la rampa al entrar en pantalla.
 */
export function StationTimeline({ data, className }: { data: TouchpointResponseAnalytics; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp(1000);
  const puntos = useMemo(() => puntosDe(data), [data]);
  const n = puntos.length;

  if (n === 0) {
    return (
      <div className={cn('rounded-[18px] border p-6 text-center text-sm', className)} style={{ background: 'var(--color-dash-night)', borderColor: '#1d1d1d', color: 'var(--color-dash-night-fg-muted)' }}>
        {t('dash.thru.empty')}
      </div>
    );
  }

  const unidad = t(data.bucket === 'hour' ? 'stations.dash.unitHour' : 'stations.dash.unitDay');
  const tercio = Math.ceil(n / 3);
  const faseDe = (i: number): ThroughputPhase => (i < tercio ? 'warmup' : i < tercio * 2 ? 'peak' : 'tail');
  const valores = puntos.map((b) => b.count);
  const total = valores.reduce((a, b) => a + b, 0);
  const pico = Math.max(0, ...valores);
  const iPico = valores.indexOf(pico);
  const techo = Math.max(pico, 1) * 1.08;
  const media = n ? total / n : 0;
  const barH = 190;
  const fmt = (ms: number) => (data.bucket === 'hour' ? dayjs(ms).format('MMM D · h A') : dayjs(ms).format('MMM D'));
  const eje = [fmt(puntos[0]!.ms), fmt(puntos[Math.floor((n - 1) / 2)]!.ms), fmt(puntos[n - 1]!.ms)];
  const marcadores = [
    { label: t('stations.dash.peakWhen', { unit: unidad }), value: fmt(puntos[iPico]!.ms) },
    { label: t('stations.dash.perUnit', { unit: unidad }), value: fmtInt(Math.round(pico * p)) },
    { label: t('stations.dash.total'), value: fmtInt(Math.round(total * p)) },
    { label: t('stations.dash.avg'), value: media >= 10 ? fmtInt(Math.round(media * p)) : (media * p).toFixed(1) },
  ];
  const picoBg = 'linear-gradient(180deg, #fde68a, var(--color-dash-amber))';

  return (
    <section ref={ref} className={cn('flex flex-col gap-[18px] rounded-[18px] border p-[22px]', className)} style={{ background: 'var(--color-dash-night)', borderColor: '#1d1d1d' }}>
      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <span className="dash-mono text-sm font-bold uppercase tracking-[0.14em] text-white">{t('stations.detail.responsesOverTime')}</span>
        <span className="dash-mono rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em]" style={{ background: 'rgba(200,229,48,0.14)', color: 'var(--color-dash-lime)' }}>
          {t('stations.dash.chip', { n: fmtInt(data.totalResponses), unit: unidad })}
        </span>
      </div>

      <div className="relative pt-4">
        <div className="relative border-t border-dashed" style={{ borderColor: 'rgba(251,191,36,0.55)' }}>
          <span className="dash-mono absolute right-0 top-[-19px] text-[10px] tracking-[0.08em]" style={{ color: '#d9a82a' }}>
            {t('stations.dash.peakCap', { n: fmtInt(pico), unit: unidad })}
          </span>
        </div>
        <div className="flex items-end gap-0.5" style={{ height: barH + 6 }}>
          {puntos.map((b, i) => {
            const fase = faseDe(i);
            const esPico = i === iPico && pico > 0;
            return (
              <div
                key={b.ms}
                title={`${fmt(b.ms)} · ${t('stations.dash.tooltip', { n: fmtInt(b.count), phase: t(`dash.phase.${fase}`) })}`}
                className="flex h-full min-w-0 flex-1 items-end"
              >
                <span
                  className="w-full rounded-t-[2px] transition-[height] duration-[600ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]"
                  style={{
                    height: `${Math.max(b.count ? 3 : 2, (b.count / techo) * barH * p)}px`,
                    background: esPico ? picoBg : PHASE_COLORS[fase],
                    opacity: b.count ? 1 : 0.35,
                    boxShadow: esPico ? '0 0 18px rgba(251,191,36,0.45)' : undefined,
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="dash-mono mt-2 flex justify-between text-[10px] tracking-[0.06em]" style={{ color: 'var(--color-dash-night-fg-subtle)' }}>
          {eje.map((e, i) => (
            <span key={i}>{e}</span>
          ))}
        </div>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
        {marcadores.map((m) => (
          <div key={m.label} className="flex flex-col gap-1 rounded-[12px] border p-4" style={{ background: 'var(--color-dash-night-2)', borderColor: 'var(--color-dash-night-border)' }}>
            <span className="dash-mono text-[22px] font-bold leading-none tabular-nums" style={{ color: 'var(--color-dash-lime)' }}>{m.value}</span>
            <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>{m.label}</span>
          </div>
        ))}
      </div>

      <div className="dash-mono flex flex-wrap gap-4 text-[11px]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>
        {(['warmup', 'peak', 'tail'] as const).map((f) => (
          <span key={f} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: PHASE_COLORS[f] }} />
            {t(`dash.phase.${f}`)}
          </span>
        ))}
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: picoBg }} />
          {t('stations.dash.legendPeak')}
        </span>
      </div>
    </section>
  );
}
